// RevenueCat helper — thin wrapper around @revenuecat/purchases-capacitor.
// All exports no-op on web so consumers can call them unconditionally.
//
// Flow:
//   • nativeBoot.ts → Purchases.configure() with the public iOS SDK key.
//   • useAuth → rcLogIn(supabaseUserId) when a session is established,
//     rcLogOut() on signout.
//   • SubscriptionTiers (native fork) → getOfferings() to render packages,
//     purchasePackage() to start a purchase, restorePurchases() for the
//     standard Apple "Restore" affordance.
//   • The revenuecat-webhook edge function does the canonical writes to
//     profiles.subscription_tier. The client mostly reflects RC state for
//     immediate UI feedback after a purchase.

import { isNative } from "@/lib/capacitor";

// Static map: RevenueCat package identifier → our internal tier id.
// Package identifiers must match exactly what's configured in the
// RevenueCat dashboard (Offerings → Current Offering → Packages).
// The values stay as the canonical tier ids that the rest of the app
// (profiles.subscription_tier, featureAccess, figVerifyTiers) recognizes.
export const PACKAGE_TO_TIER: Record<string, "collector" | "seller" | "pro_seller" | "one_off"> = {
  collector_monthly: "collector",
  seller_monthly: "seller",
  pro_seller_monthly: "pro_seller",
  oneoff_pack_10: "one_off",
};

export const TIER_TO_PACKAGE: Record<string, string> = Object.fromEntries(
  Object.entries(PACKAGE_TO_TIER).map(([pkg, tier]) => [tier, pkg]),
);

// Lazy import of the SDK so web builds don't pull the native module.
async function rc() {
  if (!isNative) return null;
  try {
    const mod = await import("@revenuecat/purchases-capacitor");
    return mod.Purchases;
  } catch (e) {
    console.warn("[rc] import failed", e);
    return null;
  }
}

export async function rcLogIn(appUserId: string): Promise<void> {
  const Purchases = await rc();
  if (!Purchases) return;
  try {
    await Purchases.logIn({ appUserID: appUserId });
  } catch (e) {
    console.warn("[rc] logIn failed", e);
  }
}

export async function rcLogOut(): Promise<void> {
  const Purchases = await rc();
  if (!Purchases) return;
  try {
    await Purchases.logOut();
  } catch (e) {
    // logOut() throws if no user is logged in — safe to ignore.
  }
}

// Returns the current Offering's packages keyed by RC identifier, plus the
// raw RC package objects (needed when calling purchasePackage). Returns null
// on web or on any SDK error.
export type RcPackage = {
  identifier: string;
  product: {
    identifier: string;
    title: string;
    description: string;
    priceString: string;
    price: number;
  };
  // The raw package — kept opaque on our side, just passed back to RC.
  raw: unknown;
};

export async function getCurrentPackages(): Promise<RcPackage[] | null> {
  const Purchases = await rc();
  if (!Purchases) return null;
  try {
    const { current } = (await Purchases.getOfferings()) as {
      current: {
        availablePackages: Array<{
          identifier: string;
          product: {
            identifier: string;
            title: string;
            description: string;
            priceString: string;
            price: number;
          };
        }> | null;
      } | null;
    };
    if (!current?.availablePackages) return [];
    return current.availablePackages.map((p) => ({
      identifier: p.identifier,
      product: {
        identifier: p.product.identifier,
        title: p.product.title,
        description: p.product.description,
        priceString: p.product.priceString,
        price: p.product.price,
      },
      raw: p,
    }));
  } catch (e) {
    console.warn("[rc] getOfferings failed", e);
    return null;
  }
}

// Returns true if the purchase completed (i.e., user has an active
// entitlement after the transaction). Throws on a hard failure that the
// caller should surface (network, store error). Returns false silently if
// the user cancelled the App Store sheet.
export async function purchasePackage(pkg: RcPackage): Promise<boolean> {
  const Purchases = await rc();
  if (!Purchases) throw new Error("In-app purchases aren't available on this platform.");
  try {
    const result = (await Purchases.purchasePackage({ aPackage: pkg.raw as Parameters<typeof Purchases.purchasePackage>[0]["aPackage"] })) as {
      customerInfo: { entitlements: { active: Record<string, unknown> } };
    };
    const active = result?.customerInfo?.entitlements?.active ?? {};
    return Object.keys(active).length > 0;
  } catch (e) {
    const err = e as { userCancelled?: boolean; message?: string };
    if (err.userCancelled) return false;
    throw e;
  }
}

export async function restorePurchases(): Promise<boolean> {
  const Purchases = await rc();
  if (!Purchases) return false;
  try {
    const result = (await Purchases.restorePurchases()) as {
      customerInfo: { entitlements: { active: Record<string, unknown> } };
    };
    const active = result?.customerInfo?.entitlements?.active ?? {};
    return Object.keys(active).length > 0;
  } catch (e) {
    console.warn("[rc] restorePurchases failed", e);
    return false;
  }
}

// iOS doesn't allow cancelling subscriptions inside the app — Apple owns
// that UI. We deep-link to the system Settings → Subscriptions sheet.
// Apple requires this affordance on any auto-renewable subscription page.
// The apps.apple.com/account/subscriptions URL is intercepted by iOS and
// routed straight to the system subscription manager.
export async function openManageSubscriptions(): Promise<void> {
  if (!isNative) return;
  window.location.href = "https://apps.apple.com/account/subscriptions";
}
